import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { usePreviewStore } from '@/rennovation/planning-view/store/usePreviewStore';
import { CreatePreviewPayload, DutyChange } from '@/rennovation/planning-view/types/api/preview-response';

export function useDutyChangePreview() {
	const previewStore = usePreviewStore();
	const { currentPreview, isPanelOpen, loading, isActive, violations, changes, affectedDrivers, affectedDuties } = storeToRefs(previewStore);

	const showBanner = computed(() => isActive.value);
	const violationsCount = computed(() => violations.value.length);
	const changesCount = computed(() => changes.value.length);

	const previewButtonLabelKey = computed(() => (isActive.value ? 'add_to_preview' : 'planning_duty_status_preview'));
	const previewButtonLoading = computed(() => !isActive.value && loading.value);
	const showRightPanel = computed(() => isPanelOpen.value);

	function togglePreviewPanel() {
		previewStore.togglePanel();
	}

	async function createPreview(payload: CreatePreviewPayload = {}) {
		return previewStore.createPreview(payload);
	}

	async function loadPreview() {
		return previewStore.loadPreview();
	}

	async function publishPreview() {
		return previewStore.publishPreview();
	}

	async function cancelPreview() {
		return previewStore.cancelPreview();
	}

	function clearPreview() {
		previewStore.clearPreview();
	}

	async function addToPreview() {
		if (!isActive.value) return;
	}

	async function create() {
		if (isActive.value) return;
		return createPreview({});
	}

	async function handlePreviewButtonClick(onAfterPreview?: () => void | Promise<void>): Promise<void> {
		if (isActive.value) {
			await addToPreview();
		} else {
			await create();
		}
		if (typeof onAfterPreview === 'function') {
			await onAfterPreview();
		}
	}

	async function updateChanges(updatedChanges: DutyChange[]): Promise<void> {
		const id = currentPreview.value?._id;
		if (!id) return;
		await previewStore.updatePreview(id, { changes: updatedChanges });
	}

	function getEditorNames(affectedByPreview: unknown): string[] {
		if (!affectedByPreview || typeof affectedByPreview !== 'object') return [];

		const names = Object.values(affectedByPreview as Record<string, string>);

		return [...new Set(names)];
	}

	async function undoChange(): Promise<void> {
		await previewStore.undoChange();
	}

	async function redoChange(): Promise<void> {
		const id = currentPreview.value?._id;
		if (!id) return;
		await previewStore.redoChange();
	}

	return {
		currentPreview,
		loading,
		showRightPanel,
		isActive,
		violations,
		changes,
		affectedDrivers,
		affectedDuties,
		showBanner,
		violationsCount,
		changesCount,
		previewButtonLabelKey,
		previewButtonLoading,
		undoChange,
		redoChange,
		getEditorNames,
		loadPreview,
		createPreview,
		publishPreview,
		cancelPreview,
		clearPreview,
		addToPreview,
		create,
		handlePreviewButtonClick,
		togglePreviewPanel,
		updateChanges,
	};
}
